import React, { useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Modal, Spinner, Alert, Collapse,
} from 'reactstrap';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import TagSelector from '../Common/TagSelector';
import { get } from '../../helpers/api_helper';
import {
  applyVariableValueToQuery,
  buildVariableState,
  hasQueryVariables,
  renderQueryWithVariables,
} from './queryVariables';

const DESCRIPTION_PLACEHOLDER_REGEX = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g;

function sanitizeFilename(value) {
  return (value || 'query-results')
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
}

function escapeCsvCell(value) {
  const stringValue = value == null ? '' : String(value);
  const escapedValue = stringValue.replace(/"/g, '""');

  if (/[",\r\n]/.test(escapedValue)) {
    return `"${escapedValue}"`;
  }

  return escapedValue;
}

function tsvToCsv(tsv) {
  if (!tsv) {
    return '';
  }

  return tsv
    .split('\n')
    .map((line) => line.split('\t').map((cell) => escapeCsvCell(cell)).join(','))
    .join('\n');
}

function formatTableValue(value) {
  if (value == null) {
    return '';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

function getRowKey(row, columns, fallbackIndex) {
  const joined = columns.map((column) => formatTableValue(row[column])).join('|');
  return joined || `row-${fallbackIndex}`;
}

function SqlTagVariableInput(props) {
  const {
    inputId,
    value,
    selectedTags,
    onChange,
  } = props;
  const [initialTags, setInitialTags] = useState([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(false);

    if (Array.isArray(selectedTags) && selectedTags.length > 0) {
      setInitialTags(selectedTags);
      setHydrated(true);
      return undefined;
    }

    const ids = Array.isArray(value)
      ? value.map((entry) => String(entry).trim()).filter(Boolean)
      : [];

    if (ids.length === 0) {
      setInitialTags([]);
      setHydrated(true);
    } else {
      let cancelled = false;

      const loadTags = async () => {
        try {
          let hydratedTags = [];

          if (ids.every((entry) => /^\d+$/.test(entry))) {
            hydratedTags = await get(`/api/items/tag/by-ids?ids=${ids.join(',')}`);
          } else {
            const tagSearches = await Promise.all(
              ids.map((entry) => get(`/api/items/tags?q=${encodeURIComponent(entry)}`)),
            );

            hydratedTags = tagSearches
              .map((matches, index) => (
                Array.isArray(matches)
                  ? matches.find((tag) => (
                    String(tag.label).toLowerCase() === ids[index].toLowerCase()
                  ))
                  : null
              ))
              .filter(Boolean);
          }

          if (!cancelled) {
            setInitialTags(Array.isArray(hydratedTags) ? hydratedTags : []);
            setHydrated(true);
          }
        } catch (error) {
          if (!cancelled) {
            setInitialTags([]);
            setHydrated(true);
          }
        }
      };

      loadTags();

      return () => {
        cancelled = true;
      };
    }

    return undefined;
  }, [selectedTags, value]);

  if (!hydrated) {
    return (
      <div className="d-inline-flex align-items-center px-3 py-2">
        <Spinner size="sm" color="secondary" />
      </div>
    );
  }

  return (
    <TagSelector
      id={inputId}
      initialTags={initialTags}
      onUpdate={onChange}
    />
  );
}

SqlTagVariableInput.defaultProps = {
  value: [],
  selectedTags: null,
};

SqlTagVariableInput.propTypes = {
  inputId: PropTypes.string.isRequired,
  value: PropTypes.array,
  selectedTags: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

function SQLResultsModal(props) {
  const {
    shown,
    loading,
    error,
    result,
    query,
    autoRunRequested,
    onEdit,
    onAutoRun,
    onRun,
    onClose,
    shareUrl,
    t,
  } = props;
  const [queryText, setQueryText] = useState(query?.query || '');
  const [filename, setFilename] = useState('');
  const [queryPreviewOpen, setQueryPreviewOpen] = useState(true);
  const [tagSelections, setTagSelections] = useState({});

  useEffect(() => {
    if (shown) {
      setQueryText(query?.query || '');
      setQueryPreviewOpen(true);
      setTagSelections({});
    }
  }, [shown, query]);

  useEffect(() => {
    if (result) {
      setQueryPreviewOpen(false);
    }
  }, [result]);

  useEffect(() => {
    if (shown && autoRunRequested && onAutoRun) {
      onAutoRun();
    }
  }, [autoRunRequested, onAutoRun, shown]);

  const variableState = useMemo(() => buildVariableState(queryText), [queryText]);
  const variables = Object.values(variableState);
  const renderedQuery = useMemo(
    () => renderQueryWithVariables(queryText, tagSelections),
    [queryText, tagSelections],
  );
  const previewQuery = result?.executed_sql || renderedQuery;
  const descriptionParts = useMemo(() => {
    const description = query?.description || '';
    const parts = [];
    let lastIndex = 0;
    DESCRIPTION_PLACEHOLDER_REGEX.lastIndex = 0;
    let match = DESCRIPTION_PLACEHOLDER_REGEX.exec(description);

    while (match) {
      if (match.index > lastIndex) {
        parts.push({
          key: `text-${lastIndex}-${match.index}`,
          type: 'text',
          value: description.slice(lastIndex, match.index),
        });
      }

      parts.push({
        key: `variable-${match[1]}-${match.index}`,
        type: 'variable',
        value: match[1],
      });
      lastIndex = match.index + match[0].length;
      match = DESCRIPTION_PLACEHOLDER_REGEX.exec(description);
    }

    if (lastIndex < description.length) {
      parts.push({
        key: `text-${lastIndex}-${description.length}`,
        type: 'text',
        value: description.slice(lastIndex),
      });
    }

    return parts;
  }, [query]);
  const describedVariableNames = useMemo(() => (
    descriptionParts
      .filter((part) => part.type === 'variable' && variableState[part.value])
      .map((part) => part.value)
  ), [descriptionParts, variableState]);
  const inlineDescriptionVariables = useMemo(() => (
    descriptionParts.some((part) => part.type === 'variable' && variableState[part.value])
  ), [descriptionParts, variableState]);
  const remainingVariables = useMemo(() => (
    variables.filter((variable) => !describedVariableNames.includes(variable.name))
  ), [describedVariableNames, variables]);

  const executedDate = result?.executed_at
    ? result.executed_at.slice(0, 10)
    : new Date().toISOString().slice(0, 10);
  const defaultFilename = `${sanitizeFilename(result?.name)}-${executedDate}.csv`;

  useEffect(() => {
    setFilename(defaultFilename);
  }, [defaultFilename, shown]);

  const downloadResults = () => {
    if (!result?.tsv) {
      return;
    }

    const csvContent = tsvToCsv(result.tsv);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeFilename = filename.trim() || defaultFilename;

    link.href = url;
    link.setAttribute('download', safeFilename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleVariableChange = (variableName, nextValue) => {
    if (Array.isArray(nextValue)) {
      setTagSelections((current) => ({
        ...current,
        [variableName]: nextValue,
      }));
    }

    setQueryText((current) => applyVariableValueToQuery(current, variableName, nextValue));
  };

  const renderVariableInput = (variable, inline = false) => {
    if (variable.type === 'tags' || variable.type === 'tags_any' || variable.type === 'tags_all') {
      return (
        <div className={inline ? 'sql-results-modal__inline-tags' : ''}>
          <SqlTagVariableInput
            inputId={`${inline ? 'sql-variable-inline' : 'sql-variable'}-${variable.name}`}
            value={Array.isArray(variable.value) ? variable.value : []}
            selectedTags={tagSelections[variable.name] || null}
            onChange={(nextValue) => handleVariableChange(variable.name, nextValue)}
          />
        </div>
      );
    }

    return (
      <input
        id={`sql-variable-${variable.name}`}
        type={variable.type}
        className={`form-control ${inline ? 'sql-results-modal__inline-input' : ''}`}
        value={variable.value}
        onChange={(event) => (
          handleVariableChange(variable.name, event.target.value)
        )}
        aria-label={variable.name}
      />
    );
  };

  const handleRun = () => {
    if (onRun) {
      onRun(renderedQuery);
    }
  };

  const copyShareLink = async () => {
    if (!shareUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      toastr.success(t('Share link copied'), t('Success'));
    } catch (clipboardError) {
      toastr.error(t('Unable to copy share link'), t('Error'));
    }
  };

  return (
    <Modal size="xl" isOpen={shown} className="sql-results-modal">
      <style>
        {`
          .sql-results-modal__stat {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            color: #212529;
          }

          .sql-results-modal__columns {
            color: #6c757d;
          }

          .sql-results-modal__textarea {
            font-family: Consolas, Monaco, "Courier New", monospace;
            font-size: 12px;
            white-space: pre;
          }

          .sql-results-modal__table-wrap {
            max-height: 32rem;
            overflow: auto;
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
          }

          .sql-results-modal__table {
            margin-bottom: 0;
            font-size: 12px;
          }

          .sql-results-modal__table th,
          .sql-results-modal__table td {
            white-space: nowrap;
            vertical-align: top;
          }

          .sql-results-modal__table code {
            color: inherit;
            font-size: inherit;
            white-space: pre;
          }

          .sql-results-modal__query-preview {
            font-family: Consolas, Monaco, "Courier New", monospace;
            font-size: 12px;
            min-height: 18rem;
            white-space: pre;
          }

          .sql-results-modal__accordion-button {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.75rem 1rem;
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
            background: #f8f9fa;
            color: #212529;
            font-weight: 600;
          }

          .sql-results-modal__accordion-button[aria-expanded="true"] {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
          }

          .sql-results-modal__accordion-body {
            border: 1px solid #dee2e6;
            border-top: 0;
            border-bottom-left-radius: 0.375rem;
            border-bottom-right-radius: 0.375rem;
            padding: 1rem;
          }

          .sql-results-modal__description {
            font-size: 1rem;
            line-height: 1.8;
          }

          .sql-results-modal__description--plain {
            color: #6c757d;
          }

          .sql-results-modal__inline-input {
            display: inline-block;
            width: auto;
            min-width: 9rem;
            max-width: 16rem;
            margin: 0 0.35rem;
            vertical-align: middle;
          }

          .sql-results-modal__inline-tags {
            display: inline-block;
            min-width: 16rem;
            max-width: 28rem;
            margin: 0 0.35rem;
            vertical-align: middle;
          }

          .sql-results-modal__inline-tags .tag-selector {
            min-height: 2.5rem;
            border: 1px solid #ced4da;
            border-radius: 0.375rem;
            background: #fff;
          }

          .sql-results-modal__run-button {
            min-width: 3.5rem;
            height: 3.5rem;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
          }

          .sql-results-modal__action-button {
            min-width: 3.5rem;
            height: 3.5rem;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 1.25rem;
          }

          body[data-layout-mode="dark"] .sql-results-modal__stat {
            background: linear-gradient(180deg, rgba(51, 52, 46, 0.98) 0%, rgba(43, 44, 39, 0.98) 100%);
            border-color: rgba(83, 84, 77, 0.85);
            color: #f3efe4;
          }

          body[data-layout-mode="dark"] .sql-results-modal__columns {
            color: #d3d0c2;
          }

          body[data-layout-mode="dark"] .sql-results-modal__textarea {
            background: #252621;
            border-color: #393a34;
            color: #f4f1e6;
          }

          body[data-layout-mode="dark"] .sql-results-modal__textarea:focus {
            background: #252621;
            color: #f4f1e6;
          }

          body[data-layout-mode="dark"] .sql-results-modal__table-wrap {
            border-color: #393a34;
            background: #252621;
          }

          body[data-layout-mode="dark"] .sql-results-modal__table {
            color: #f4f1e6;
          }

          body[data-layout-mode="dark"] .sql-results-modal__table thead th {
            background: #2d2e28;
            border-color: #393a34;
            color: #f4f1e6;
          }

          body[data-layout-mode="dark"] .sql-results-modal__table td {
            background: #252621;
            border-color: #393a34;
          }

          body[data-layout-mode="dark"] .sql-results-modal__table.table-striped tbody tr:nth-of-type(odd) td {
            background: #2a2b26;
          }

          body[data-layout-mode="dark"] .sql-results-modal__query-preview {
            background: #252621;
            border-color: #393a34;
            color: #f4f1e6;
          }

          body[data-layout-mode="dark"] .sql-results-modal__query-preview:focus {
            background: #252621;
            color: #f4f1e6;
          }

          body[data-layout-mode="dark"] .sql-results-modal__accordion-button {
            background: linear-gradient(180deg, rgba(51, 52, 46, 0.98) 0%, rgba(43, 44, 39, 0.98) 100%);
            border-color: rgba(83, 84, 77, 0.85);
            color: #f3efe4;
          }

          body[data-layout-mode="dark"] .sql-results-modal__accordion-body {
            background: rgba(37, 38, 33, 0.9);
            border-color: rgba(83, 84, 77, 0.85);
          }

          body[data-layout-mode="dark"] .sql-results-modal__description {
            color: #f3efe4;
          }

          body[data-layout-mode="dark"] .sql-results-modal__description--plain {
            color: #d3d0c2;
          }

          body[data-layout-mode="dark"] .sql-results-modal__inline-tags .tag-selector {
            border-color: #393a34;
            background: #252621;
          }
        `}
      </style>
      <div className="modal-header">
        <h5 className="modal-title mt-0">
          {query?.name ? `${t('Query Results')}: ${query.name}` : t('Query Results')}
        </h5>
        <button
          onClick={onClose}
          type="button"
          className="close"
          data-dismiss="modal"
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div className="modal-body">
        {!loading && query && (
          <>
            {hasQueryVariables(queryText) && inlineDescriptionVariables && (
              <div className="mb-4">
                <div className="sql-results-modal__description">
                  {descriptionParts.map((part) => {
                    if (part.type === 'text') {
                      return <span key={part.key}>{part.value}</span>;
                    }

                    const variable = variableState[part.value];

                    if (!variable) {
                      return <span key={part.key}>{`{{${part.value}}}`}</span>;
                    }

                    return (
                      <React.Fragment key={part.key}>
                        {renderVariableInput(variable, true)}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            {hasQueryVariables(queryText) && remainingVariables.length > 0 && (
              <div className="mb-4">
                <h6 className="mb-3">{t('Variables')}</h6>
                <div className="row g-3">
                  {remainingVariables.map((variable) => (
                    <div className="col-md-4" key={variable.name}>
                      <label htmlFor={`sql-variable-${variable.name}`} className="form-label mb-1">
                        {variable.name}
                      </label>
                      {renderVariableInput(variable)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {hasQueryVariables(queryText) && !inlineDescriptionVariables && query?.description && (
              <div className="mb-3 sql-results-modal__description sql-results-modal__description--plain">
                {query.description}
              </div>
            )}

            <div className="mb-0">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <button
                  type="button"
                  className="sql-results-modal__accordion-button text-start me-3"
                  onClick={() => setQueryPreviewOpen((current) => !current)}
                  aria-expanded={queryPreviewOpen}
                >
                  <span>{t('Query Preview')}</span>
                  <span>{queryPreviewOpen ? t('Hide') : t('Show')}</span>
                </button>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    onClick={copyShareLink}
                    className="btn btn-soft-secondary sql-results-modal__action-button"
                    aria-label={t('Copy Share Link')}
                    title={t('Copy Share Link')}
                    disabled={!shareUrl}
                  >
                    <i className="mdi mdi-link-variant" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={onEdit}
                    className="btn btn-soft-info sql-results-modal__action-button"
                    aria-label={t('Edit Query')}
                    title={t('Edit Query')}
                  >
                    <i className="mdi mdi-pencil" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={handleRun}
                    className="btn btn-primary sql-results-modal__run-button"
                    aria-label={t('Run Query')}
                    title={t('Run Query')}
                  >
                    <i className="mdi mdi-play" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <Collapse isOpen={queryPreviewOpen}>
                <div className="sql-results-modal__accordion-body">
                  <textarea
                    className="form-control sql-results-modal__query-preview"
                    rows="18"
                    value={previewQuery}
                    readOnly
                  />
                </div>
              </Collapse>
            </div>
          </>
        )}

        {loading && (
          <div className="text-center py-5">
            <Spinner style={{ width: '4rem', height: '4rem' }} type="border" color="info" />
            <p className="mt-3 mb-0 text-muted">{t('Running query...')}</p>
          </div>
        )}

        {!loading && error && (
          <Alert color="danger" className="mb-0">
            {error}
          </Alert>
        )}

        {!loading && !error && result && (
          <>
            <div className="d-flex flex-wrap gap-3 mb-3">
              <div className="sql-results-modal__stat rounded px-3 py-2">
                <strong>{t('Rows')}</strong>
                {' '}
                {result.row_count}
              </div>
              <div className="sql-results-modal__stat rounded px-3 py-2">
                <strong>{t('Columns')}</strong>
                {' '}
                {result.columns?.length || 0}
              </div>
              <div className="sql-results-modal__stat rounded px-3 py-2">
                <strong>{t('Duration')}</strong>
                {' '}
                {result.duration_ms}
                ms
              </div>
            </div>
            <div className="sql-results-modal__columns mb-2 small">
              {result.columns?.length > 0 ? result.columns.join(', ') : t('No columns returned')}
            </div>
            <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
              <div className="flex-grow-1">
                <label htmlFor="sql-results-filename" className="form-label mb-1">
                  {t('Filename')}
                </label>
                <input
                  id="sql-results-filename"
                  type="text"
                  className="form-control"
                  value={filename}
                  onChange={(event) => setFilename(event.target.value)}
                />
              </div>
              <div>
                <button
                  type="button"
                  onClick={downloadResults}
                  className="btn btn-primary"
                  aria-label={t('Download CSV')}
                  title={t('Download CSV')}
                >
                  <i className="mdi mdi-download me-1" aria-hidden="true" />
                  .csv
                </button>
              </div>
            </div>
            {result.rows?.length > 0 ? (
              <div className="table-responsive sql-results-modal__table-wrap">
                <table className="table table-striped table-sm sql-results-modal__table">
                  <thead>
                    <tr>
                      {result.columns.map((column) => (
                        <th key={column} scope="col">{column}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((row, rowIndex) => (
                      <tr key={getRowKey(row, result.columns, rowIndex)}>
                        {result.columns.map((column) => (
                          <td key={`${getRowKey(row, result.columns, rowIndex)}-${column}`}>
                            <code>{formatTableValue(row[column])}</code>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Alert color="secondary" className="mb-0">
                {t('Query completed with no rows returned')}
              </Alert>
            )}
          </>
        )}
      </div>
      <div className="modal-footer">
        <button
          type="button"
          onClick={onClose}
          className="btn btn-soft-dark"
          data-dismiss="modal"
        >
          {t('Close')}
        </button>
      </div>
    </Modal>
  );
}

SQLResultsModal.defaultProps = {
  shown: false,
  loading: false,
  error: null,
  result: null,
  query: null,
  autoRunRequested: false,
  onEdit: null,
  onAutoRun: null,
  onRun: null,
  shareUrl: '',
  t: (x) => (x),
};

SQLResultsModal.propTypes = {
  shown: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.string,
  result: PropTypes.object,
  query: PropTypes.object,
  autoRunRequested: PropTypes.bool,
  onEdit: PropTypes.func,
  onAutoRun: PropTypes.func,
  onRun: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  shareUrl: PropTypes.string,
  t: PropTypes.any,
};

export default connect(() => ({

}), {})(withTranslation()(SQLResultsModal));
